import React, { useEffect, useRef, useState } from 'react';
import { ShieldCheck, Trophy, Sparkles, XCircle, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { gamificationAPI } from '../services/api';

const PAGE_SIZE = 30;

const currentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start: fmt(start), end: fmt(end) };
};

const formatDate = (value, withTime = true) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', withTime ? { dateStyle: 'short', timeStyle: 'short' } : { dateStyle: 'long' }).format(parsed);
};

const TYPE_LABELS = {
  conquista: 'Conquista (registro anterior)',
  avanco_diario: 'Avanço diário',
  conexao_positiva: 'Conexão positiva com outro membro',
  pequena_meta: 'Pequena meta concluída',
  contribuicao_mastermind: 'Contribuição extraordinária para o Mastermind',
  grande_meta: 'Grande meta concluída',
};

const typeLabel = (type) => TYPE_LABELS[type] || 'Não pontuável';

const TypeBadge = ({ type }) => {
  if (type === 'grande_meta') {
    return (
      <Badge className="border border-amber-400/30 bg-amber-950/70 text-amber-200">
        <Trophy size={12} className="mr-1" /> Grande meta concluída
      </Badge>
    );
  }
  if (TYPE_LABELS[type]) {
    return (
      <Badge className="border border-indigo-400/30 bg-indigo-950/70 text-indigo-200">
        <Sparkles size={12} className="mr-1" /> {TYPE_LABELS[type]}
      </Badge>
    );
  }
  return <Badge className="border border-gray-700 bg-gray-900 text-gray-400">Não pontuável</Badge>;
};

const ReviewButton = ({ recordId, onReviewed, approve = false }) => {
  const busy = useRef(false);
  const [open, setOpen] = useState(false);
  const label = approve ? "Aprovar" : "Reprovar";
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleReview = async () => {
    if (busy.current) return;
    busy.current = true;
    setSubmitting(true);
    try {
      await (approve ? gamificationAPI.approveAchievement : gamificationAPI.rejectAchievement)(recordId, reason.trim() || undefined);
      toast.success(approve ? 'Relato aprovado — pontos creditados.' : 'Relato reprovado — pontos removidos.');
      setOpen(false);
      setReason('');
      onReviewed?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível revisar este relato.');
    } finally {
      busy.current = false;
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(value) => { if (!busy.current) setOpen(value); }}>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="outline" className={approve ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20" : "border-red-500/30 bg-red-500/10 text-red-200 hover:bg-red-500/20"}>
          <ShieldCheck size={14} className="mr-1" /> {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="glass-card border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">{label} este relato?</AlertDialogTitle>
          <AlertDialogDescription className="text-neutral-400">
            {approve ? "Os pontos do tipo reivindicado serão creditados no ranking." : "Os pontos serão zerados e removidos do ranking."} A decisão, o responsável e o motivo ficam registrados no histórico.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <label className="text-xs text-neutral-400">Motivo (opcional)</label>
          <Textarea
            maxLength={500}
            disabled={submitting}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Ex.: não é proporcional ao nível declarado"
            className="bg-neutral-900/50 border-neutral-800 text-white text-sm"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting} className="bg-neutral-800 text-white border-neutral-700">Cancelar</AlertDialogCancel>
          <Button onClick={handleReview} disabled={submitting} className={approve ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}>
            {submitting ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
            {label}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const LoadingCards = () => (
  <div className="space-y-3">
    {[1, 2, 3, 4].map((item) => (
      <Skeleton key={`audit-skeleton-${item}`} className="h-24 w-full bg-white/10" />
    ))}
  </div>
);

const AdminGameAuditPage = () => {
  const defaultRange = currentMonthRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [name, setName] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const requestId = useRef(0);

  const load = async () => {
    setLoading(true);
    const currentRequest = ++requestId.current;
    try {
      const response = await gamificationAPI.getAudit({ start: startDate, end: endDate, name, status: status || undefined, page, pageSize: PAGE_SIZE });
      if (currentRequest !== requestId.current) return;
      setItems(response.data?.items || []);
      setTotal(response.data?.total || 0);
    } catch (error) {
      if (currentRequest !== requestId.current) return;
      setItems([]);
      setTotal(0);
      toast.error(error?.response?.data?.detail || 'Não foi possível carregar a auditoria.');
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, name, status, page]);

  const handleRangeChange = (setter) => (event) => {
    setPage(1);
    setter(event.target.value);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStartIndex = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEndIndex = Math.min(page * PAGE_SIZE, total);

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-game-audit-page">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white">Game Audit</h1>
          <p className="text-gray-400 mt-1">
            Aprove ou reprove relatos e acompanhe o histórico das decisões administrativas.
          </p>
        </div>

        <Card className="border-gray-800 bg-gray-900">
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <ShieldCheck size={18} className="text-emerald-300" />
              Relatos avaliados
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Input aria-label="Filtrar por nome" placeholder="Nome do membro" value={name} onChange={handleRangeChange(setName)} className="w-48 bg-gray-950 border-gray-800 text-white" />
              <select aria-label="Filtrar por status" value={status} onChange={handleRangeChange(setStatus)} className="h-10 rounded-md bg-gray-950 border border-gray-800 text-white px-2">
                <option value="">Todos os status</option>
                <option value="approved">Aprovados</option>
                <option value="rejected">Reprovados</option>
                <option value="admin_approved">Aprovados por admin</option>
                <option value="admin_rejected">Reprovados por admin</option>
              </select>
              <Input
                type="date"
                value={startDate}
                onChange={handleRangeChange(setStartDate)}
                className="w-40 bg-gray-950 border-gray-800 text-white"
                aria-label="Data inicial do período"
              />
              <span className="text-gray-500 text-sm">até</span>
              <Input
                type="date"
                value={endDate}
                onChange={handleRangeChange(setEndDate)}
                className="w-40 bg-gray-950 border-gray-800 text-white"
                aria-label="Data final do período"
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-gray-400">Período pela data do convite, incluindo as duas datas.</p>
            {loading ? (
              <LoadingCards />
            ) : items.length === 0 ? (
              <div className="rounded-lg border border-gray-800 bg-gray-950 px-4 py-6 text-center text-gray-300">
                Nenhum relato avaliado neste período.
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((item) => {
                  const isRejected = item.admin_action === 'rejected';
                  return (
                    <div key={item.id} className="rounded-lg border border-white/10 bg-gray-800/50 p-4 space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white">{item.full_name}</span>
                          <TypeBadge type={item.final_type} />
                          {item.admin_action === "approved" && <Badge>Aprovado por auditor</Badge>}
                          {!item.admin_action && <Badge>{(item.points_awarded || 0) > 0 ? "Aprovado" : "Reprovado"}</Badge>}
                          {isRejected && (
                            <Badge className="border border-red-500/30 bg-red-950/70 text-red-200">
                              <XCircle size={12} className="mr-1" /> Reprovado por auditor
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-slate-400">Convite: {item.invite_date?.split("-").reverse().join("/")} · Avaliação: {formatDate(item.evaluated_at || item.filled_at)}</span>
                      </div>
                      <p className="text-sm text-white">{item.description || '—'}</p>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-slate-400">
                          Reivindicado como <strong>{typeLabel(item.claimed_type)}</strong> · {item.points_awarded ?? 0} pontos
                        </p>
                        {!isRejected && (item.points_awarded || 0) > 0 && (
                          <ReviewButton recordId={item.id} onReviewed={load} />
                        )}
                      </div>
                      {(item.points_awarded || 0) === 0 && <ReviewButton recordId={item.id} approve onReviewed={load} />}
                      {item.reasoning && (
                        <p className="text-xs text-slate-500 italic">IA: "{item.reasoning}"</p>
                      )}
                      {item.admin_action && (
                        <div className="border-t border-white/10 pt-2 text-xs text-slate-300 space-y-1">
                          <p>Última ação: {item.admin_action === 'approved' ? 'aprovação' : 'reprovação'} por {item.admin_action_by_name || item.admin_action_by || 'administrador'} em {formatDate(item.admin_action_at)}</p>
                          {(item.admin_history || []).map((entry, index) => (
                            <p key={index}>{formatDate(entry.at)} · {entry.by_name || entry.by} · {entry.action === 'approved' ? 'Aprovou' : 'Reprovou'} · {entry.previous_points} → {entry.points_awarded} pontos{entry.reason ? ` · ${entry.reason}` : ''}</p>
                          ))}
                        </div>
                      )}
                      {item.admin_action_reason && (
                        <p className="text-xs text-red-300 italic">Auditor: "{item.admin_action_reason}"</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {total > 0 && (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pt-2">
                <p className="text-xs text-gray-500">
                  Mostrando {rangeStartIndex}–{rangeEndIndex} de {total} relato{total === 1 ? '' : 's'}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-gray-800 text-gray-300 hover:text-white"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || loading}
                  >
                    <ChevronLeft size={14} className="mr-1" />
                    Anterior
                  </Button>
                  <span className="text-xs text-gray-400 px-1">
                    Página {page} de {totalPages}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-gray-800 text-gray-300 hover:text-white"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || loading}
                  >
                    Próxima
                    <ChevronRight size={14} className="ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminGameAuditPage;
